import { createOpenAI } from "@ai-sdk/openai";

/**
 * BYOK (Bring Your Own Key) - Secure API Key Management
 * 
 * Users must provide their own OpenAI API key for security.
 * This is more secure than using a shared platform key because:
 * - Keys never leave the user's browser
 * - No central point of failure
 * - Users control their own costs
 * - Each user's key is isolated
 */
export const getApiKey = () => {
  // BYOK only - users manage their own keys
  const userKey = localStorage.getItem("openai_api_key");
  
  if (!userKey) {
    throw new Error(
      "OpenAI API key is required. " +
      "Please configure your API key (BYOK) in Settings → AI & Integration."
    );
  }
  
  // Validate the key format
  if (userKey.startsWith('k-proj-')) {
    throw new Error(
      "Invalid API key format. " +
      "You entered an Organization ID (k-proj-...), but you need an API Key (sk-...). " +
      "Get your API key at https://platform.openai.com/api-keys"
    );
  }
  
  if (!userKey.startsWith('sk-')) {
    throw new Error(
      "Invalid API key format. " +
      "API keys should start with 'sk-'. " +
      "Get your API key at https://platform.openai.com/api-keys"
    );
  }
  
  return userKey;
};

/**
 * Get the source of the currently used API key
 * Returns 'byok' if user has configured their key, 'none' otherwise
 */
export const getApiKeySource = (): 'byok' | 'none' => {
  const userKey = localStorage.getItem("openai_api_key");
  return userKey ? 'byok' : 'none';
};

export const createOpenAIProvider = () => {
  const apiKey = getApiKey();
  
  if (!apiKey) {
    throw new Error(
      "OpenAI API key is required. " +
      "Please configure your API key (BYOK) in Settings."
    );
  }
  
  return createOpenAI({ apiKey });
};
